<template>
  <controller>
    <page-header title="Ajustes Cadastrais">
      <template v-slot:side>
        <button class="queue-refresh" type="button" :disabled="loading" @click="load">
          {{ loading ? 'Atualizando...' : 'Atualizar' }}
        </button>
      </template>
    </page-header>

    <section class="main registration-adjustments">
      <div v-if="error" class="feedback error">{{ error }}</div>
      <div v-if="message" class="feedback success">{{ message }}</div>

      <div class="queue-toolbar">
        <div class="status-tabs" aria-label="Filtrar solicitações">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            class="status-tab"
            :class="{ active: status === option.value }"
            type="button"
            @click="changeStatus(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <span class="queue-count">{{ solicitacoes.length }} solicitação{{ solicitacoes.length === 1 ? '' : 'es' }}</span>
      </div>

      <section class="ticket-board">
        <header class="ticket-board-header">
          <div>
            <strong>Fila de análise</strong>
            <span>{{ currentStatusLabel }}</span>
          </div>
          <small>Selecione um ticket para comparar os dados e decidir.</small>
        </header>

        <div v-if="loading" class="empty-state">
          Carregando solicitações cadastrais...
        </div>

        <div v-else-if="!solicitacoes.length" class="empty-state ticket-empty">
          <strong>Nenhuma solicitação {{ currentStatusLabel.toLowerCase() }} encontrada.</strong>
          <span>Quando um responsável enviar um ajuste cadastral, ele aparecerá aqui para análise.</span>
        </div>

        <div v-else class="ticket-list">
          <template v-for="item in solicitacoes" :key="item.idSolicitacaoAjusteCadastral">
            <button
              class="ticket-row"
              :class="{ active: isSelected(item) }"
              type="button"
              :disabled="saving"
              :aria-expanded="Boolean(isSelected(item))"
              :aria-controls="isSelected(item) ? `ticket-detail-${item.idSolicitacaoAjusteCadastral}` : undefined"
              @click="selectSolicitacao(item)"
            >
              <span class="ticket-id">#{{ item.idSolicitacaoAjusteCadastral }}</span>
              <span class="ticket-main">
                <strong>{{ item.nomeAlvo || 'Cadastro sem nome' }}</strong>
                <small>{{ typeLabel(item.tipoPessoa) }} · {{ item.unidadeSigla || item.unidade || 'Unidade não informada' }}</small>
              </span>
              <span class="ticket-requester">
                <small>Solicitante</small>
                {{ item.nomeSolicitante || 'Responsável' }}
              </span>
              <span class="ticket-date">
                <small>Recebido em</small>
                {{ formatDate(item.criadoEm || item.createdAt || item.created_at) }}
              </span>
              <span class="ticket-changes">{{ changesCount(item) }} campo{{ changesCount(item) === 1 ? '' : 's' }}</span>
              <span class="status-badge" :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
              <span class="ticket-toggle">{{ isSelected(item) ? 'Recolher' : 'Ver detalhes' }}</span>
            </button>
            <section v-if="isSelected(item)" :id="`ticket-detail-${item.idSolicitacaoAjusteCadastral}`" class="ticket-detail" :aria-label="`Detalhes da solicitação #${item.idSolicitacaoAjusteCadastral}`">
              <header class="detail-header">
                <div>
                  <span class="ticket-id">#{{ selected.idSolicitacaoAjusteCadastral }}</span>
                  <h2>{{ selected.nomeAlvo || 'Cadastro' }}</h2>
                  <p>
                    {{ typeLabel(selected.tipoPessoa) }} ·
                    {{ selected.unidadeSigla || selected.unidade || 'Unidade não informada' }}
                  </p>
                </div>
                <div class="detail-meta">
                  <span class="status-badge" :class="statusClass(selected.status)">{{ statusLabel(selected.status) }}</span>
                  <small>Solicitado por {{ selected.nomeSolicitante || 'responsável' }}</small>
                </div>
              </header>

              <section class="request-proofs" aria-label="Comprovantes da solicitação">
                <h3>Comprovantes enviados</h3>
                <p v-if="!proofEntries.length">Esta solicitação não possui comprovantes anexados.</p>
                <article v-for="proof in proofEntries" :key="proof.section" class="request-proof">
                  <strong>{{ proof.section === 'Documento' ? 'Documento de identificação' : 'Comprovante de endereço' }}</strong>
                  <span>{{ proof.nome }}</span>
                  <small v-if="proof.iddocumentoEntregue">Registrado nos documentos entregues. A validação documental é realizada separadamente.</small>
                  <small v-else-if="selected.status === 'reprovado'">Mantido no histórico da solicitação. Os documentos entregues não foram alterados.</small>
                  <small v-else>Anexo da solicitação. Será incorporado aos documentos entregues após a aprovação.</small>
                  <button class="queue-refresh" type="button" :disabled="proofLoading === proof.section" @click="viewProof(proof)">
                    {{ proofLoading === proof.section ? 'Carregando...' : 'Visualizar comprovante' }}
                  </button>
                  <p v-if="proofErrors[proof.section]" class="feedback error" role="alert">{{ proofErrors[proof.section] }}</p>
                  <document-file-preview preview v-if="proofImages[proof.section]" :file="proofImages[proof.section].file" :mime="proofImages[proof.section].mime" :label="proof.nome" />
                </article>
              </section>

              <div class="comparison-head">
                <span>Campo</span>
                <span>Cadastro atual</span>
                <span>Solicitado</span>
              </div>

              <div class="diff-section" v-for="section in sectionsWithChanges" :key="section.key">
                <h3>{{ section.label }}</h3>
                <div class="diff-row" v-for="change in section.changes" :key="section.key + change.field">
                  <strong>{{ change.label }}</strong>
                  <span>{{ displayValue(change.before, change.field) }}</span>
                  <span class="requested-value">{{ displayValue(change.after, change.field) }}</span>
                </div>
              </div>

              <div v-if="!sectionsWithChanges.length" class="empty-state compact">
                Nenhum campo alterado foi identificado no payload desta solicitação.
              </div>

              <div v-if="selected.status === 'pendente' && canApprove" class="decision-panel">
                <div class="decision-copy">
                  <strong>Decisão do ticket</strong>
                  <span>Aprovar atualiza o cadastro no Apollo. Reprovar envia a justificativa para o responsável.</span>
                </div>

                <div class="decision-actions" v-if="!showRejectForm">
                  <button class="decision-button reject-button" type="button" :disabled="saving" @click="openRejectForm">
                    Reprovar
                  </button>
                  <button class="decision-button approve-button" type="button" :disabled="saving" @click="aprovar">
                    {{ saving ? 'Aprovando...' : 'Aprovar alterações' }}
                  </button>
                </div>

                <div class="reject-box" v-else>
                  <label>
                    Motivo da recusa
                    <textarea
                      v-model="justificativa"
                      rows="4"
                      placeholder="Explique de forma objetiva o que precisa ser corrigido pelo responsável."
                    ></textarea>
                  </label>
                  <div class="decision-actions">
                    <button class="decision-button neutral-button" type="button" :disabled="saving" @click="cancelReject">
                      Cancelar
                    </button>
                    <button class="decision-button reject-confirm-button" type="button" :disabled="saving" @click="reprovar">
                      {{ saving ? 'Reprovando...' : 'Confirmar recusa' }}
                    </button>
                  </div>
                </div>
              </div>

              <div v-else-if="selected.status === 'pendente'" class="decision-panel readonly-panel">
                <div class="decision-copy">
                  <strong>Solicitação pendente</strong>
                  <span>Seu usuário não possui permissão para aprovar ou recusar este ajuste cadastral.</span>
                </div>
              </div>
            </section>
          </template>
        </div>
      </section>
    </section>
  </controller>
</template>

<script>
import CentralFamiliaService from '@/services/central-familia.service'
import { ADDRESS_NUMBER_ERROR, isAddressNumberValid } from '@/services/address-number.service'
import UserService from '@/services/user.service'
import DocumentFilePreview from '@/components/utilities/DocumentFilePreview.vue'

export default {
  components: { DocumentFilePreview },
  data() {
    return {
      proofLoading: null,
      proofImages: {},
      proofErrors: {},
      status: 'pendente',
      loading: false,
      saving: false,
      solicitacoes: [],
      selected: null,
      justificativa: '',
      showRejectForm: false,
      canApprove: false,
      error: '',
      message: '',
      statusOptions: [
        { value: 'pendente', label: 'Pendentes' },
        { value: 'aprovado', label: 'Aprovados' },
        { value: 'reprovado', label: 'Reprovados' },
        { value: '', label: 'Todos' }
      ],
      sections: [
        { key: 'Basicos', label: 'Dados pessoais', fields: ['nomeDocumento', 'nome', 'dataNascimento', 'sexo', 'email', 'telefonefixo', 'telefonecelular'] },
        { key: 'Documento', label: 'Documentos', fields: ['cpf', 'rg', 'rgEmissor'] },
        { key: 'Endereco', label: 'Endereço', fields: ['cep', 'logradouro', 'numero', 'complemento', 'bairro'] },
        { key: 'Aluno', label: 'Dados escolares', fields: ['escolaAnterior', 'emailAcademico'] }
      ]
    }
  },
  computed: {
    proofEntries() {
      const proofs = this.selected?.comprovantes || {}
      return ['Documento', 'Endereco'].filter((section) => proofs[section]).map((section) => ({ ...proofs[section], section }))
    },
    currentStatusLabel() {
      const option = this.statusOptions.find((item) => item.value === this.status)
      return option ? option.label : 'Todos'
    },
    sectionsWithChanges() {
      if (!this.selected) return []

      return this.sections
        .map((section) => ({
          ...section,
          changes: section.fields
            .filter((field) => this.hasRequestedField(section.key, field))
            .map((field) => ({
              field,
              label: this.fieldLabel(field),
              before: this.originalValue(section.key, field),
              after: this.requestedValue(section.key, field)
            }))
            .filter((change) => this.hasChanged(change.before, change.after, change.field))
        }))
        .filter((section) => section.changes.length)
    }
  },
  mounted() {
    this.canApprove = UserService.filterPermissions('aprovarAjusteCadastro')
    this.load()
  },
  methods: {
    async viewProof(proof) {
      const requestId = this.selected?.idSolicitacaoAjusteCadastral
      this.proofLoading = proof.section
      this.proofErrors[proof.section] = ''
      try {
        const response = await CentralFamiliaService.obterComprovante(requestId, proof.section)
        const { file, mime } = response.data || {}
        if (typeof file !== 'string' || !/^[A-Za-z0-9+/]+={0,2}$/.test(file) || !['image/jpeg', 'image/png', 'application/pdf'].includes(mime)) throw new Error('invalid_image')
        const image = { file, mime }
        if (this.selected?.idSolicitacaoAjusteCadastral === requestId) this.proofImages[proof.section] = image
      } catch {
        if (this.selected?.idSolicitacaoAjusteCadastral === requestId) this.proofErrors[proof.section] = 'Não foi possível carregar o comprovante. Tente novamente.'
      } finally {
        if (this.selected?.idSolicitacaoAjusteCadastral === requestId) this.proofLoading = null
      }
    },
    changeStatus(value) {
      if (this.status === value) return
      this.status = value
      this.load()
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const response = await CentralFamiliaService.listarAjustesCadastrais(this.status)
        this.canApprove = Boolean(response.data.canApprove)
        this.solicitacoes = (response.data.data || []).filter((item) => this.shouldShowSolicitacao(item))
        this.selected = null
        this.justificativa = ''
        this.showRejectForm = false
      } catch (error) {
        this.error = error.response?.data?.message || 'Nao foi possivel carregar as solicitações cadastrais.'
      } finally {
        this.loading = false
      }
    },
    selectSolicitacao(item) {
      if (this.saving) return
      const wasSelected = this.isSelected(item)
      this.proofImages = {}
      this.proofErrors = {}
      this.proofLoading = null
      this.selected = wasSelected ? null : item
      this.justificativa = ''
      this.showRejectForm = false
      this.error = ''
      this.message = ''
    },
    isSelected(item) {
      return this.selected && this.selected.idSolicitacaoAjusteCadastral === item.idSolicitacaoAjusteCadastral
    },
    shouldShowSolicitacao(item) {
      return item.status !== 'pendente' || this.changesCount(item) > 0
    },
    payload(source) {
      if (!this.selected) return {}
      return this.parsePayload(this.selected[source])
    },
    payloadFrom(item, source) {
      return this.parsePayload(item ? item[source] : null)
    },
    parsePayload(value) {
      if (!value) return {}
      if (typeof value === 'object') return value
      try {
        return JSON.parse(value)
      } catch (e) {
        return {}
      }
    },
    originalValue(section, field) {
      const payload = this.payload('payloadOriginal')
      return payload[section] ? payload[section][field] : null
    },
    requestedValue(section, field) {
      const payload = this.payload('payloadSolicitado')
      return payload[section] ? payload[section][field] : null
    },
    hasPayloadField(payload, section, field) {
      const sectionPayload = payload[section]
      return Boolean(sectionPayload && Object.prototype.hasOwnProperty.call(sectionPayload, field))
    },
    hasRequestedField(section, field) {
      return this.hasPayloadField(this.payload('payloadSolicitado'), section, field)
    },
    changesCount(item) {
      let count = 0
      const original = this.payloadFrom(item, 'payloadOriginal')
      const requested = this.payloadFrom(item, 'payloadSolicitado')

      this.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (!this.hasPayloadField(requested, section.key, field)) return

          const originalSection = original[section.key] || {}
          const requestedSection = requested[section.key] || {}
          if (this.hasChanged(originalSection[field], requestedSection[field], field)) {
            count += 1
          }
        })
      })

      return count
    },
    hasChanged(before, after, field) {
      const normalizedBefore = this.normalizedFieldValue(field, before)
      const normalizedAfter = this.normalizedFieldValue(field, after)
      return normalizedBefore !== normalizedAfter
    },
    normalizedFieldValue(field, value) {
      if (field === 'dataNascimento') {
        return this.normalizeDateValue(value)
      }

      if (['cpf', 'cep', 'telefonefixo', 'telefonecelular'].includes(field)) {
        return String(value ?? '').replace(/\D/g, '')
      }

      if (field === 'rg') {
        return String(value ?? '').replace(/[^\dXx]/g, '').toUpperCase()
      }

      if (field === 'email' || field === 'emailAcademico') {
        return String(value ?? '').trim().toLowerCase()
      }

      return String(value ?? '').trim()
    },
    normalizeDateValue(value) {
      const text = String(value ?? '').trim()
      if (!text) return ''

      const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`

      const brMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (brMatch) return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`

      return text
    },
    displayValue(value, field) {
      if (field === 'dataNascimento') {
        return this.formatDateOnly(value)
      }

      const text = String(value ?? '').trim()
      return text || '-'
    },
    formatDateOnly(value) {
      const normalized = this.normalizeDateValue(value)
      const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!match) return normalized || '-'

      return `${match[3]}/${match[2]}/${match[1]}`
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    typeLabel(value) {
      const labels = {
        aluno: 'Aluno',
        responsavel: 'Responsável'
      }

      return labels[value] || value || 'Pessoa'
    },
    statusLabel(value) {
      const labels = {
        pendente: 'Pendente',
        aprovado: 'Aprovado',
        reprovado: 'Reprovado'
      }

      return labels[value] || value || 'Sem status'
    },
    statusClass(value) {
      return {
        pending: value === 'pendente',
        approved: value === 'aprovado',
        rejected: value === 'reprovado'
      }
    },
    fieldLabel(field) {
      const labels = {
        nomeDocumento: 'Nome completo',
        nome: 'Nome social',
        dataNascimento: 'Data de nascimento',
        sexo: 'Sexo',
        email: 'E-mail',
        telefonefixo: 'Telefone fixo',
        telefonecelular: 'Celular',
        cpf: 'CPF',
        rg: 'RG/CIN',
        rgEmissor: 'Órgão emissor',
        cep: 'CEP',
        logradouro: 'Logradouro',
        numero: 'Número',
        complemento: 'Complemento',
        bairro: 'Bairro',
        escolaAnterior: 'Escola anterior',
        emailAcademico: 'E-mail acadêmico'
      }

      return labels[field] || field
    },
    openRejectForm() {
      if (!this.canApprove) {
        this.error = 'Seu usuário não possui permissão para reprovar ajustes cadastrais.'
        return
      }

      this.showRejectForm = true
      this.error = ''
      this.message = ''
    },
    cancelReject() {
      this.showRejectForm = false
      this.justificativa = ''
      this.error = ''
    },
    async aprovar() {
      if (!this.selected) return
      if (!isAddressNumberValid(this.requestedValue('Endereco', 'numero'))) {
        this.error = ADDRESS_NUMBER_ERROR
        return
      }
      if (!this.canApprove) {
        this.error = 'Seu usuário não possui permissão para aprovar ajustes cadastrais.'
        return
      }

      if (!this.sectionsWithChanges.length) {
        this.error = 'Esta solicitação não possui alterações para aprovar.'
        return
      }

      this.saving = true
      this.error = ''
      this.message = ''
      try {
        await CentralFamiliaService.aprovarAjusteCadastral(this.selected.idSolicitacaoAjusteCadastral)
        this.message = 'Solicitação aprovada e cadastro atualizado.'
        await this.load()
      } catch (error) {
        this.error = error.response?.data?.message || 'Nao foi possivel aprovar a solicitação.'
      } finally {
        this.saving = false
      }
    },
    async reprovar() {
      if (!this.selected) return
      if (!this.canApprove) {
        this.error = 'Seu usuário não possui permissão para reprovar ajustes cadastrais.'
        return
      }

      if (!this.justificativa.trim()) {
        this.error = 'Informe o motivo da recusa para que o responsável saiba o que corrigir.'
        return
      }

      this.saving = true
      this.error = ''
      this.message = ''
      try {
        await CentralFamiliaService.reprovarAjusteCadastral(this.selected.idSolicitacaoAjusteCadastral, this.justificativa)
        this.message = 'Solicitação recusada e responsável notificado.'
        await this.load()
      } catch (error) {
        this.error = error.response?.data?.message || 'Nao foi possivel reprovar a solicitação.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.request-proofs { margin: 20px 0; }
.request-proof { display: grid; gap: 10px; padding: 16px; margin-top: 12px; border: 1px solid #e1e3e9; border-radius: 12px; overflow-wrap: anywhere; }
.request-proof img { max-width: 100%; max-height: 75vh; object-fit: contain; justify-self: start; }
.request-proof button { justify-self: start; }
.request-proof small { color: #626975; }

.registration-adjustments {
  padding: 20px;
}

.queue-refresh {
  background: #fff;
  border: 1px solid #d8dce3;
  border-radius: 8px;
  color: #303848;
  font-weight: 800;
  min-height: 36px;
  padding: 0 14px;
}

.feedback {
  border-radius: 8px;
  font-weight: 700;
  margin-bottom: 12px;
  padding: 12px 14px;
}

.feedback.error {
  background: #fff0f0;
  color: #e30613;
}

.feedback.success {
  background: #edf9f1;
  color: #1f7a3b;
}

.queue-toolbar {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.status-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
}

.status-tab {
  background: #fff;
  border: 1px solid #d8dce3;
  border-radius: 8px;
  color: #4c5668;
  cursor: pointer;
  font-weight: 800;
  min-height: 36px;
  padding: 0 14px;
  white-space: nowrap;
}

.status-tab.active {
  background: #e30613;
  border-color: #e30613;
  color: #fff;
}

.queue-count {
  color: #7b8494;
  font-weight: 800;
  white-space: nowrap;
}

.ticket-board,
.ticket-detail {
  background: #fff;
  border: 1px solid #e1e4ea;
  border-radius: 8px;
  padding: 16px;
}

.ticket-board {
  margin-bottom: 16px;
}

.ticket-board-header,
.detail-header,
.decision-panel,
.decision-actions {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.ticket-board-header {
  border-bottom: 1px solid #e8ebf0;
  margin-bottom: 10px;
  padding-bottom: 12px;
}

.ticket-board-header div {
  display: grid;
  gap: 2px;
}

.ticket-board-header strong,
.decision-copy strong {
  color: #303848;
  font-size: 15px;
}

.ticket-board-header span,
.ticket-board-header small,
.ticket-main small,
.ticket-requester small,
.ticket-date small,
.detail-header p,
.detail-meta small,
.decision-copy span {
  color: #7b8494;
}

.ticket-list {
  display: grid;
  gap: 8px;
}

.ticket-row {
  align-items: center;
  background: #fff;
  border: 1px solid #e1e4ea;
  border-radius: 8px;
  color: #303848;
  cursor: pointer;
  display: flex;
  gap: 12px;
  min-height: 68px;
  padding: 10px 12px;
  text-align: left;
  transition: border-color 0.16s ease, box-shadow 0.16s ease;
}

.ticket-row:focus-visible {
  outline: 2px solid #e30613;
  outline-offset: 2px;
}

.ticket-row:disabled {
  cursor: wait;
}

.ticket-toggle {
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.ticket-row:hover,
.ticket-row.active {
  border-color: #e30613;
  box-shadow: 0 4px 14px rgba(227, 6, 19, 0.08);
}

.ticket-id {
  color: #e30613;
  font-weight: 900;
  min-width: 54px;
}

.ticket-main {
  display: grid;
  gap: 3px;
  min-width: 220px;
  width: 28%;
}

.ticket-requester,
.ticket-date {
  display: grid;
  gap: 3px;
  min-width: 170px;
}

.ticket-changes {
  background: #f3f5f8;
  border-radius: 999px;
  color: #303848;
  font-weight: 800;
  padding: 6px 10px;
  white-space: nowrap;
}

.status-badge {
  border-radius: 999px;
  font-weight: 900;
  margin-left: auto;
  padding: 6px 10px;
  white-space: nowrap;
}

.status-badge.pending {
  background: #fff5da;
  color: #9b6a00;
}

.status-badge.approved {
  background: #e6f7ed;
  color: #1f7a3b;
}

.status-badge.rejected {
  background: #fff0f0;
  color: #c90010;
}

.detail-header {
  border-bottom: 1px solid #e8ebf0;
  margin-bottom: 14px;
  padding-bottom: 14px;
}

.detail-header h2,
.detail-header p,
.diff-section h3 {
  margin: 0;
}

.detail-header h2 {
  color: #303848;
  font-size: 22px;
  margin-top: 4px;
}

.detail-meta {
  align-items: flex-end;
  display: grid;
  gap: 6px;
  justify-items: end;
}

.comparison-head {
  background: #f7f8fb;
  border-radius: 8px;
  color: #7b8494;
  display: grid;
  font-size: 12px;
  font-weight: 900;
  gap: 12px;
  grid-template-columns: 190px minmax(0, 1fr) minmax(0, 1fr);
  margin-bottom: 10px;
  padding: 10px 12px;
  text-transform: uppercase;
}

.diff-section {
  border: 1px solid #eef0f4;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.diff-section h3 {
  background: #fbfcfe;
  color: #303848;
  font-size: 13px;
  padding: 10px 12px;
}

.diff-row {
  display: grid;
  gap: 12px;
  grid-template-columns: 190px minmax(0, 1fr) minmax(0, 1fr);
  border-bottom: 1px solid #f0f1f4;
  padding: 10px 12px;
}

.diff-row:last-child {
  border-bottom: 0;
}

.diff-row strong,
.diff-row span {
  word-break: break-word;
}

.requested-value {
  color: #e30613;
  font-weight: 800;
}

.decision-panel {
  background: #f7f8fb;
  border: 1px solid #e1e4ea;
  border-radius: 8px;
  margin-top: 16px;
  padding: 14px;
}

.readonly-panel {
  align-items: flex-start;
}

.decision-copy,
.reject-box,
.reject-box label {
  display: grid;
  gap: 8px;
}

.decision-button {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 900;
  justify-content: center;
  min-height: 40px;
  min-width: 128px;
  padding: 0 18px;
}

.decision-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.reject-button {
  background: #fff;
  border-color: #e30613;
  color: #e30613;
}

.reject-button:hover {
  background: #fff0f0;
}

.reject-confirm-button,
.approve-button {
  background: #e30613;
  border-color: #e30613;
  color: #fff;
}

.reject-confirm-button:hover,
.approve-button:hover {
  background: #c90010;
  border-color: #c90010;
}

.neutral-button {
  background: #fff;
  border-color: #d8dce3;
  color: #303848;
}

.reject-box {
  flex: 1;
}

.reject-box label {
  color: #303848;
  font-weight: 900;
}

.reject-box textarea {
  border: 1px solid #d8dce3;
  border-radius: 8px;
  min-height: 94px;
  padding: 10px;
  resize: vertical;
}

.empty-state {
  color: #7b8494;
  font-weight: 700;
  padding: 18px 0;
}

.ticket-empty {
  align-items: center;
  display: grid;
  gap: 4px;
  justify-items: center;
  min-height: 160px;
  text-align: center;
}

.empty-state.compact {
  background: #f7f8fb;
  border-radius: 8px;
  padding: 12px;
}

@media (max-width: 900px) {
  .queue-toolbar,
  .ticket-board-header,
  .detail-header,
  .decision-panel,
  .decision-actions,
  .ticket-row {
    align-items: stretch;
    flex-direction: column;
  }

  .ticket-main,
  .ticket-requester,
  .ticket-date {
    min-width: 0;
    width: 100%;
  }

  .status-badge {
    margin-left: 0;
  }

  .comparison-head {
    display: none;
  }

  .diff-row {
    grid-template-columns: 1fr;
  }

  .diff-row strong::after {
    color: #7b8494;
    content: ' atual / solicitado';
    display: block;
    font-size: 11px;
    font-weight: 800;
    margin-top: 3px;
  }
}
</style>
